import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Globe, Users, Briefcase, Mail, Phone, Building2, ArrowRight, ArrowLeft } from 'lucide-react';

const PublicCompanyProfile = () => {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrg();
  }, [slug]);

  const fetchOrg = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/orgs/${slug}`);
      if (res.data.success) setOrg(res.data.data);
    } catch (err) {
      console.error('Error fetching org:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-lg">Company not found</p>
      </div>
    );
  }

  const infoItems = [
    { icon: Building2, label: 'Industry', value: org.industry },
    { icon: Users, label: 'Company Size', value: org.company_size },
    { icon: MapPin, label: 'Location', value: [org.city, org.country].filter(Boolean).join(', ') },
    { icon: Globe, label: 'Website', value: org.website, isLink: true },
    { icon: Mail, label: 'Email', value: org.email },
    { icon: Phone, label: 'Phone', value: org.phone },
  ].filter(item => item.value);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-company-page">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Back link */}
          <Link
            to={`/${slug}/jobs`}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 mb-8 transition-colors"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to open positions
          </Link>

          <div className="flex items-start gap-6">
            {org.logo_url ? (
              <img src={org.logo_url} alt={org.name} className="w-20 h-20 rounded-xl object-cover border border-gray-200" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-10 h-10 text-blue-400" />
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-1" data-testid="company-profile-name">{org.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2">
                {org.industry && (
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />{org.industry}
                  </span>
                )}
                {(org.city || org.country) && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />{[org.city, org.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {org.company_size && (
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4" />{org.company_size} employees
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* About Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">About {org.name}</h2>
          {org.description ? (
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{org.description}</p>
          ) : (
            <p className="text-gray-400 italic">No company description available yet.</p>
          )}
        </div>

        {/* Company Details */}
        {infoItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {infoItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{item.label}</p>
                      {item.isLink ? (
                        <a
                          href={item.value.startsWith('http') ? item.value : `https://${item.value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline font-medium"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-800 font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Open Positions CTA */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Open Positions</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {org.open_positions_count || 0} position{(org.open_positions_count || 0) !== 1 ? 's' : ''} currently open
              </p>
            </div>
            <Link
              to={`/${slug}/jobs`}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="view-positions-btn"
            >
              <Briefcase className="w-4 h-4" />
              View Positions
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCompanyProfile;
